import { useEffect, useMemo, useState } from "react";
import { platformApi, type PlatformTenant } from "@/core/api/platformApi";
import {
  platformNotificationsApi,
  type PlatformNotificationDashboard,
  type PlatformNotificationDelivery,
  type PlatformSmsProviderSettings,
  type PlatformSmsProvidersSettings,
  type SmsProviderCode,
  type SmsSenderLine,
} from "@/core/api/platformNotificationsApi";
import type { AutomationSettings } from "@/core/types/tenantWorkspace";
import { getErrorMessage } from "@/core/utils/errors";
import { AppIcon } from "@/shared/components/AppIcon";

type Tab = "overview" | "provider" | "templates" | "send" | "reports";

const defaultAutomation: AutomationSettings = {
  isEnabled: true,
  emailEnabled: true,
  smsEnabled: false,
  socialEnabled: false,
  messageSubject: "معرفی {{tenantName}}",
  messageTemplate:
    "سلام {{contactName}}، برای مشاهده معرفی و کاتالوگ {{tenantName}} از این لینک استفاده کنید: {{catalogLink}}",
};

const tabs: Array<{
  key: Tab;
  label: string;
  icon: "grid" | "settings" | "document" | "send" | "activity";
}> = [
  { key: "overview", label: "نمای کلی", icon: "grid" },
  { key: "provider", label: "سرویس‌دهنده و خطوط", icon: "settings" },
  { key: "templates", label: "ارسال خودکار شرکت‌ها", icon: "document" },
  { key: "send", label: "ارسال آزمایشی", icon: "send" },
  { key: "reports", label: "صف و گزارش", icon: "activity" },
];

const faNumber = (
  value: number | null | undefined,
  maximumFractionDigits = 0,
): string =>
  new Intl.NumberFormat("fa-IR", { maximumFractionDigits }).format(value ?? 0);

const hasSmsEntitlement = (tenant: PlatformTenant): boolean =>
  tenant.subscription.smsEnabled && tenant.subscription.smsCreditEnabled;

const hasAvailableSmsCredit = (tenant: PlatformTenant): boolean =>
  hasSmsEntitlement(tenant) &&
  (tenant.subscription.smsCreditUnlimited ||
    (tenant.subscription.smsCreditRemaining ?? 0) > 0);

const faDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(parsed);
};

const statusMeta: Record<string, { label: string; tone: string }> = {
  pending: { label: "در صف", tone: "bg-amber-50 text-amber-700" },
  sent: { label: "ارسال‌شده", tone: "bg-emerald-50 text-emerald-700" },
  failed: { label: "ناموفق", tone: "bg-rose-50 text-rose-700" },
  skipped: { label: "ردشده", tone: "bg-slate-100 text-slate-600" },
  requiresConfiguration: {
    label: "نیازمند تنظیم",
    tone: "bg-violet-50 text-violet-700",
  },
};

const providerLabel = (code?: string | null): string =>
  code === "kavenegar" ? "کاوه‌نگار" : code === "sms-ir" ? "SMS.ir" : "—";

const Toggle = ({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}): React.ReactElement => (
  <button
    aria-checked={checked}
    aria-label={label}
    className={`relative h-8 w-14 shrink-0 rounded-full transition disabled:cursor-wait disabled:opacity-50 ${checked ? "bg-emerald-500" : "bg-slate-200"}`}
    disabled={disabled}
    onClick={onChange}
    role="switch"
    type="button"
  >
    <span
      className={`absolute top-1 size-6 rounded-full bg-white shadow transition ${checked ? "left-1" : "left-7"}`}
    />
  </button>
);

const Metric = ({
  label,
  value,
  hint,
  tone = "bg-white",
}: {
  label: string;
  value: string;
  hint: string;
  tone?: string;
}): React.ReactElement => (
  <article
    className={`rounded-3xl border border-slate-200 p-5 shadow-sm ${tone}`}
  >
    <p className="text-xs font-bold text-slate-500">{label}</p>
    <p className="mt-2 text-2xl font-black text-ink-950">{value}</p>
    <p className="mt-2 text-[11px] leading-5 text-slate-400">{hint}</p>
  </article>
);

export const PlatformNotificationsPage = (): React.ReactElement => {
  const [tab, setTab] = useState<Tab>("overview");
  const [dashboard, setDashboard] =
    useState<PlatformNotificationDashboard | null>(null);
  const [provider, setProvider] = useState<PlatformSmsProviderSettings | null>(
    null,
  );
  const [providersSettings, setProvidersSettings] =
    useState<PlatformSmsProvidersSettings | null>(null);
  const [selectedProviderCode, setSelectedProviderCode] =
    useState<SmsProviderCode>("sms-ir");
  const [routingForm, setRoutingForm] = useState<{
    defaultProviderCode: SmsProviderCode;
    fallbackEnabled: boolean;
    fallbackProviderCode: SmsProviderCode | null;
  }>({
    defaultProviderCode: "sms-ir",
    fallbackEnabled: false,
    fallbackProviderCode: "kavenegar",
  });
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [deliveries, setDeliveries] = useState<PlatformNotificationDelivery[]>(
    [],
  );
  const [lines, setLines] = useState<SmsSenderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeTone, setNoticeTone] = useState<"success" | "warning">(
    "success",
  );
  const [providerForm, setProviderForm] = useState({
    isEnabled: false,
    senderNumber: "",
    apiKey: "",
    clearApiKey: false,
  });
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [automation, setAutomation] =
    useState<AutomationSettings>(defaultAutomation);
  const [automationLoadedFor, setAutomationLoadedFor] = useState("");
  const [sendForm, setSendForm] = useState({
    tenantId: "",
    mobile: "",
    message: "این یک پیام آزمایشی از سامانه CRM است.",
  });
  const [filters, setFilters] = useState({ tenantId: "", status: "" });

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardResult, providersResult, tenantResult, deliveryResult] = await Promise.all(
        [
          platformNotificationsApi.dashboard(),
          platformNotificationsApi.providers(),
          platformApi.list(),
          platformNotificationsApi.deliveries({ take: 100 }),
        ],
      );
      setDashboard(dashboardResult);
      setProvidersSettings(providersResult);
      const selectedProvider =
        providersResult.providers.find(
          (item) => item.code === providersResult.defaultProviderCode,
        ) ?? providersResult.providers[0] ?? null;
      setSelectedProviderCode(
        selectedProvider?.code ?? providersResult.defaultProviderCode,
      );
      setProvider(selectedProvider);
      setRoutingForm({
        defaultProviderCode: providersResult.defaultProviderCode,
        fallbackEnabled: providersResult.fallbackEnabled,
        fallbackProviderCode:
          providersResult.fallbackProviderCode ??
          (providersResult.defaultProviderCode === "sms-ir"
            ? "kavenegar"
            : "sms-ir"),
      });
      setProviderForm({
        isEnabled: selectedProvider?.isEnabled ?? false,
        senderNumber: selectedProvider?.senderNumber ?? "",
        apiKey: "",
        clearApiKey: false,
      });
      setTenants(tenantResult);
      setDeliveries(deliveryResult);
      const firstTenant = tenantResult[0]?.id ?? "";
      const firstSmsTenant = tenantResult.find(hasAvailableSmsCredit)?.id ?? "";
      setSelectedTenantId((current) => current || firstTenant);
      setSendForm((current) => ({
        ...current,
        tenantId: current.tenantId || firstSmsTenant,
      }));
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const tenantNames = useMemo(
    () => Object.fromEntries(tenants.map((tenant) => [tenant.id, tenant.name])),
    [tenants],
  );
  const smsEnabledCount = tenants.filter(
    (tenant) => tenant.subscription.smsEnabled,
  ).length;
  const smsReadyCount = tenants.filter(hasAvailableSmsCredit).length;
  const selectedSendTenant = tenants.find(
    (tenant) => tenant.id === sendForm.tenantId,
  );
  const canSendTest =
    providersSettings?.isConfigured === true &&
    selectedSendTenant !== undefined &&
    hasAvailableSmsCredit(selectedSendTenant);
  const selectedAutomationTenant = tenants.find(
    (tenant) => tenant.id === selectedTenantId,
  );
  const canConfigureTenantSms =
    selectedAutomationTenant !== undefined &&
    hasSmsEntitlement(selectedAutomationTenant);
  const canConfigureTenantEmail =
    selectedAutomationTenant?.channels.emailAllowed === true;
  const canConfigureTenantSocial =
    selectedAutomationTenant?.channels.socialAllowed === true;
  const recent = deliveries.slice(0, 8);
  const activeProvider = providersSettings?.providers.find(
    (item) => item.code === providersSettings.defaultProviderCode,
  );

  const showNotice = (
    message: string,
    tone: "success" | "warning" = "success",
  ): void => {
    setError(null);
    setNoticeTone(tone);
    setNotice(message);
  };

  const showError = (exception: unknown): void => {
    setNotice(null);
    setError(getErrorMessage(exception));
  };

  const saveProvider = async (): Promise<void> => {
    setBusy("provider-save");
    setError(null);
    setNotice(null);
    try {
      const saved = await platformNotificationsApi.updateProvider(
        selectedProviderCode,
        {
          isEnabled: providerForm.isEnabled,
          senderNumber: providerForm.senderNumber.trim() || null,
          apiKey: providerForm.apiKey.trim() || null,
          clearApiKey: providerForm.clearApiKey,
        },
      );
      setProvider(saved);
      setProvidersSettings((current) =>
        current
          ? {
              ...current,
              providers: current.providers.map((item) =>
                item.code === saved.code ? saved : item,
              ),
              isConfigured:
                current.defaultProviderCode === saved.code
                  ? saved.isConfigured
                  : current.isConfigured,
            }
          : current,
      );
      setProviderForm({
        isEnabled: saved.isEnabled,
        senderNumber: saved.senderNumber ?? "",
        apiKey: "",
        clearApiKey: false,
      });
      showNotice(`تنظیمات ${saved.provider} با موفقیت و به‌صورت امن ذخیره شد.`);
      const refreshed = await platformNotificationsApi.providers();
      setProvidersSettings(refreshed);
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const testProvider = async (): Promise<void> => {
    setBusy("provider-test");
    setError(null);
    setNotice(null);
    try {
      const result = await platformNotificationsApi.testProvider(
        selectedProviderCode,
      );
      showNotice(
        result.success
          ? `اتصال برقرار است؛ اعتبار ${faNumber(result.credit, 3)} و ${faNumber(result.lineCount)} خط دریافت شد.`
          : result.message,
        result.success ? "success" : "warning",
      );
      const refreshed = await platformNotificationsApi.providers();
      setProvidersSettings(refreshed);
      setProvider(
        refreshed.providers.find((item) => item.code === selectedProviderCode) ??
          null,
      );
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const syncLines = async (): Promise<void> => {
    setBusy("lines");
    setError(null);
    setNotice(null);
    try {
      const result = await platformNotificationsApi.lines(
        selectedProviderCode,
      );
      setLines(result);
      showNotice(
        `${faNumber(result.length)} خط از ${provider?.provider ?? "سرویس‌دهنده"} دریافت شد. خط موردنظر را انتخاب و تنظیمات را ذخیره کنید.`,
      );
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const selectProvider = (code: SmsProviderCode): void => {
    const selected = providersSettings?.providers.find(
      (item) => item.code === code,
    );
    if (!selected) return;
    setSelectedProviderCode(code);
    setProvider(selected);
    setLines([]);
    setProviderForm({
      isEnabled: selected.isEnabled,
      senderNumber: selected.senderNumber ?? "",
      apiKey: "",
      clearApiKey: false,
    });
  };

  const saveRouting = async (): Promise<void> => {
    setBusy("routing-save");
    setError(null);
    setNotice(null);
    try {
      const saved = await platformNotificationsApi.updateRouting(routingForm);
      setProvidersSettings(saved);
      setRoutingForm({
        defaultProviderCode: saved.defaultProviderCode,
        fallbackEnabled: saved.fallbackEnabled,
        fallbackProviderCode:
          saved.fallbackProviderCode ??
          (saved.defaultProviderCode === "sms-ir" ? "kavenegar" : "sms-ir"),
      });
      showNotice("مسیر ارسال پیامک و سرویس‌دهنده پشتیبان ذخیره شد.");
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const loadAutomation = async (tenantId: string): Promise<void> => {
    if (!tenantId) return;
    setBusy("automation-load");
    setError(null);
    try {
      const result = await platformApi.automation(tenantId);
      setAutomation(result);
      setAutomationLoadedFor(tenantId);
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (
      tab === "templates" &&
      selectedTenantId &&
      automationLoadedFor !== selectedTenantId
    )
      void loadAutomation(selectedTenantId);
  }, [tab, selectedTenantId, automationLoadedFor]);

  const saveAutomation = async (): Promise<void> => {
    if (!selectedTenantId) return;
    setBusy("automation-save");
    setError(null);
    setNotice(null);
    try {
      const result = await platformApi.updateAutomation(
        selectedTenantId,
        automation,
      );
      setAutomation(result);
      setAutomationLoadedFor(selectedTenantId);
      showNotice(
        `قالب و سناریوی «${tenantNames[selectedTenantId] ?? "شرکت"}» ذخیره شد.`,
      );
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async (): Promise<void> => {
    setBusy("send");
    setError(null);
    setNotice(null);
    try {
      const delivery = await platformNotificationsApi.sendTest({
        tenantId: sendForm.tenantId,
        mobile: sendForm.mobile.trim(),
        message: sendForm.message.trim(),
      });
      setDeliveries((current) => [
        delivery,
        ...current.filter((item) => item.id !== delivery.id),
      ]);
      setTenants(await platformApi.list());
      const status = statusMeta[delivery.status]?.label ?? delivery.status;
      showNotice(
        `نتیجه ارسال آزمایشی: ${status}${delivery.error ? ` — ${delivery.error}` : ""}`,
        delivery.status === "sent" ? "success" : "warning",
      );
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const refreshReports = async (): Promise<void> => {
    setBusy("reports");
    setError(null);
    try {
      setDeliveries(
        await platformNotificationsApi.deliveries({
          tenantId: filters.tenantId || undefined,
          status: filters.status || undefined,
          take: 200,
        }),
      );
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  const retry = async (deliveryId: string): Promise<void> => {
    setBusy(`retry-${deliveryId}`);
    setError(null);
    setNotice(null);
    try {
      const updated = await platformNotificationsApi.retry(deliveryId);
      setDeliveries((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      showNotice(
        `تلاش مجدد انجام شد؛ نتیجه: ${statusMeta[updated.status]?.label ?? updated.status}.`,
        updated.status === "sent" ? "success" : "warning",
      );
    } catch (exception) {
      showError(exception);
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="min-h-[calc(100vh-74px)] text-slate-900" dir="rtl">
      <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-7">
        <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-bold text-brand-600">
              Notification Provider
            </p>
            <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">
              مدیریت پیامک و اعلان‌ها
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500">
              سرویس‌دهنده‌های پیامک، مسیر اصلی و پشتیبان، ارسال خودکار معرفی
              سرنخ و نتیجه ارسال‌ها را از این بخش کنترل کنید. قالب پیام‌های
              سیستمی در بخش «قالب‌های پیام» قرار دارد.
            </p>
          </div>
          <span
            className={`w-fit rounded-full px-4 py-2 text-xs font-black ${providersSettings?.isConfigured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}
          >
            {providersSettings?.isConfigured
              ? "زیرساخت آماده ارسال است"
              : "تنظیم سرویس کامل نیست"}
          </span>
        </section>

        <nav
          className="flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm"
          aria-label="مدیریت اعلان‌ها"
        >
          {tabs.map((item) => (
            <button
              className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-black transition ${tab === item.key ? "bg-brand-600 text-white shadow-lg shadow-brand-500/20" : "text-slate-500 hover:bg-slate-50"}`}
              key={item.key}
              onClick={() => setTab(item.key)}
              type="button"
            >
              <AppIcon name={item.icon} size={16} />
              {item.label}
            </button>
          ))}
        </nav>

        {error ? (
          <div className="flex min-w-0 items-start justify-between gap-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
            <span className="min-w-0 break-words leading-6">{error}</span>
            <button
              className="shrink-0"
              onClick={() => setError(null)}
              type="button"
            >
              <AppIcon name="close" size={17} />
            </button>
          </div>
        ) : null}
        {notice ? (
          <div
            className={`flex min-w-0 items-start gap-2 rounded-2xl border px-4 py-3 text-sm font-bold leading-7 ${noticeTone === "success" ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-amber-100 bg-amber-50 text-amber-700"}`}
          >
            <AppIcon
              className="mt-1 shrink-0"
              name={noticeTone === "success" ? "check" : "activity"}
              size={17}
            />
            <span className="min-w-0 break-words">{notice}</span>
          </div>
        ) : null}
        {loading ? (
          <div className="h-56 animate-pulse rounded-3xl bg-white shadow-sm" />
        ) : null}

        {!loading && tab === "overview" ? (
          <div className="space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                label="شرکت‌های آماده ارسال"
                value={`${faNumber(smsReadyCount)} / ${faNumber(tenants.length)}`}
                hint={`${faNumber(smsEnabledCount)} شرکت مجوز پیامک دارند؛ شارژ نیز باید فعال و موجود باشد`}
                tone="bg-emerald-50/40"
              />
              <Metric
                label="ارسال موفق امروز"
                value={faNumber(dashboard?.sentToday)}
                hint={`${faNumber(dashboard?.totalMessages)} پیامک در کل`}
              />
              <Metric
                label="ناموفق امروز"
                value={faNumber(dashboard?.failedToday)}
                hint={`${faNumber(dashboard?.requiresConfigurationToday)} مورد نیازمند تنظیم`}
                tone="bg-rose-50/30"
              />
              <Metric
                label={`اعتبار ${activeProvider?.provider ?? "سرویس اصلی"}`}
                value={
                  activeProvider?.lastKnownCredit == null
                    ? "نامشخص"
                    : faNumber(activeProvider.lastKnownCredit, 3)
                }
                hint={`${faNumber(activeProvider?.lastKnownLineCount)} خط در آخرین تست · ${activeProvider?.creditUnit ?? ""}`}
              />
            </section>
            <section
              className={`rounded-3xl border p-5 sm:p-6 ${providersSettings?.isConfigured ? "border-emerald-100 bg-emerald-50/40" : "border-amber-100 bg-amber-50/50"}`}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-black text-ink-950">
                    وضعیت سرویس اصلی {activeProvider?.provider ?? "پیامک"}
                  </h2>
                  <p className="mt-2 text-sm leading-7 text-slate-500">
                    {activeProvider?.lastConnectionTestMessage ??
                      "هنوز تست اتصال ثبت نشده است."}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    آخرین تست: {faDate(activeProvider?.lastConnectionTestAtUtc)} · خط
                    پیش‌فرض:{" "}
                    <span dir="ltr">{activeProvider?.senderNumber ?? "—"}</span>
                  </p>
                </div>
                <button
                  className="rounded-xl bg-slate-950 px-4 py-3 text-xs font-black text-white"
                  onClick={() => setTab("provider")}
                  type="button"
                >
                  بازکردن تنظیمات سرویس
                </button>
              </div>
            </section>
            <ResponsiveDeliveryTable
              deliveries={recent}
              tenantNames={tenantNames}
              busy={busy}
              onRetry={retry}
              compact
            />
          </div>
        ) : null}

        {!loading && tab === "provider" ? (
          <div className="space-y-6">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-sm font-bold text-brand-600">Provider Routing</p>
                  <h2 className="mt-1 text-xl font-black text-ink-950">مسیر ارسال پیامک</h2>
                  <p className="mt-2 max-w-3xl text-xs leading-6 text-slate-400">
                    چند سرویس را هم‌زمان تنظیم کنید، یکی را مسیر اصلی قرار دهید و در صورت نیاز سرویس دوم را پشتیبان کنید. برای جلوگیری از پیام تکراری، پشتیبان فقط وقتی استفاده می‌شود که سرویس اصلی پیکربندی نشده باشد.
                  </p>
                </div>
                <button
                  className="rounded-xl bg-slate-950 px-4 py-3 text-xs font-black text-white disabled:opacity-50"
                  disabled={busy !== null}
                  onClick={() => void saveRouting()}
                  type="button"
                >
                  {busy === "routing-save" ? "در حال ذخیره…" : "ذخیره مسیر ارسال"}
                </button>
              </div>
              <div className="mt-6 grid gap-3 md:grid-cols-2">
                {providersSettings?.providers.map((item) => (
                  <button
                    className={`rounded-2xl border p-4 text-right transition ${selectedProviderCode === item.code ? "border-brand-400 bg-brand-50 ring-2 ring-brand-100" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
                    key={item.code}
                    onClick={() => selectProvider(item.code)}
                    type="button"
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-sm font-black text-ink-950">{item.provider}</span>
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-black ${item.isConfigured ? "bg-emerald-100 text-emerald-700" : item.isEnabled ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"}`}>
                        {item.isConfigured ? "آماده" : item.isEnabled ? "ناقص" : "غیرفعال"}
                      </span>
                    </span>
                    <span className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold">
                      {routingForm.defaultProviderCode === item.code ? <span className="rounded-full bg-brand-600 px-2.5 py-1 text-white">مسیر اصلی</span> : null}
                      {routingForm.fallbackEnabled && routingForm.fallbackProviderCode === item.code ? <span className="rounded-full bg-violet-100 px-2.5 py-1 text-violet-700">پشتیبان</span> : null}
                      <span className="rounded-full bg-white px-2.5 py-1 text-slate-500">برای تنظیم کلیک کنید</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-5 grid gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 lg:grid-cols-[1fr_auto_1fr] lg:items-end">
                <label>
                  <span className="mb-2 block text-xs font-bold text-slate-500">سرویس‌دهنده اصلی</span>
                  <select
                    className="input"
                    onChange={(event) => {
                      const code = event.target.value as SmsProviderCode;
                      setRoutingForm((current) => ({
                        ...current,
                        defaultProviderCode: code,
                        fallbackProviderCode: current.fallbackProviderCode === code ? (code === "sms-ir" ? "kavenegar" : "sms-ir") : current.fallbackProviderCode,
                      }));
                    }}
                    value={routingForm.defaultProviderCode}
                  >
                    {providersSettings?.providers.map((item) => <option key={item.code} value={item.code}>{item.provider}</option>)}
                  </select>
                </label>
                <label className="flex h-12 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 text-xs font-black text-slate-700">
                  <span>پشتیبان فعال</span>
                  <Toggle checked={routingForm.fallbackEnabled} label="فعال بودن سرویس پشتیبان" onChange={() => setRoutingForm((current) => ({ ...current, fallbackEnabled: !current.fallbackEnabled }))} />
                </label>
                <label>
                  <span className="mb-2 block text-xs font-bold text-slate-500">سرویس‌دهنده پشتیبان</span>
                  <select
                    className="input"
                    disabled={!routingForm.fallbackEnabled}
                    onChange={(event) => setRoutingForm((current) => ({ ...current, fallbackProviderCode: event.target.value as SmsProviderCode }))}
                    value={routingForm.fallbackProviderCode ?? ""}
                  >
                    {providersSettings?.providers.filter((item) => item.code !== routingForm.defaultProviderCode).map((item) => <option key={item.code} value={item.code}>{item.provider}</option>)}
                  </select>
                </label>
              </div>
            </section>

            <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)]">
              <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-bold text-brand-600">{provider?.provider ?? "سرویس‌دهنده"}</p>
                    <h2 className="mt-1 text-xl font-black text-ink-950">تنظیمات امن Provider</h2>
                    <p className="mt-2 text-xs leading-6 text-slate-400">کلید API هر سرویس جداگانه در Backend رمزنگاری می‌شود و هیچ‌گاه دوباره در فرانت نمایش داده نمی‌شود.</p>
                  </div>
                  <Toggle checked={providerForm.isEnabled} label={`فعال بودن ${provider?.provider ?? "سرویس"}`} onChange={() => setProviderForm((current) => ({ ...current, isEnabled: !current.isEnabled }))} />
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <label>
                    <span className="mb-2 block text-xs font-bold text-slate-500">خط پیش‌فرض {provider?.senderNumberRequired ? "(الزامی)" : "(اختیاری)"}</span>
                    <input className="input" dir="ltr" inputMode="numeric" onChange={(event) => setProviderForm((current) => ({ ...current, senderNumber: event.target.value }))} placeholder="3000xxxxxxxx" value={providerForm.senderNumber} />
                    {!provider?.senderNumberRequired ? <span className="mt-1.5 block text-[10px] leading-5 text-slate-400">اگر خالی باشد، خط پیش‌فرض حساب Provider استفاده می‌شود.</span> : null}
                  </label>
                  <label>
                    <span className="mb-2 flex items-center justify-between gap-2 text-xs font-bold text-slate-500"><span>API Key</span>{provider?.hasApiKey && !providerForm.clearApiKey ? <span className="text-emerald-600">ذخیره شده</span> : null}</span>
                    <input autoComplete="new-password" className="input" dir="ltr" onChange={(event) => setProviderForm((current) => ({ ...current, apiKey: event.target.value, clearApiKey: false }))} placeholder={provider?.hasApiKey && !providerForm.clearApiKey ? "برای حفظ کلید فعلی خالی بگذارید" : `کلید API ${provider?.provider ?? "سرویس"}`} type="password" value={providerForm.apiKey} />
                  </label>
                </div>
                <div className="mt-4 grid gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4 sm:grid-cols-2">
                  <div><p className="text-[10px] font-bold text-slate-400">API Key ذخیره‌شده</p><p className={`mt-1 text-xs font-black ${provider?.hasApiKey && !providerForm.clearApiKey ? "text-emerald-700" : "text-slate-500"}`}>{provider?.hasApiKey && !providerForm.clearApiKey ? "••••••••••••  ذخیره شده" : "ثبت نشده"}</p></div>
                  <div><p className="text-[10px] font-bold text-slate-400">خط ذخیره‌شده</p><p className="mt-1 font-sans text-xs font-black text-slate-700" dir="ltr">{provider?.senderNumber ?? "ثبت نشده"}</p></div>
                </div>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  {provider?.hasApiKey ? <button className="text-right text-xs font-bold text-rose-600" onClick={() => setProviderForm((current) => ({ ...current, apiKey: "", clearApiKey: !current.clearApiKey, isEnabled: current.clearApiKey ? current.isEnabled : false }))} type="button">{providerForm.clearApiKey ? "انصراف از حذف کلید" : "حذف کلید ذخیره‌شده"}</button> : <span className="text-xs text-slate-400">برای شروع، API Key را وارد کنید.</span>}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button className="rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-black text-slate-600 disabled:opacity-50" disabled={busy !== null || !provider?.hasApiKey} onClick={() => void testProvider()} type="button">{busy === "provider-test" ? "در حال تست…" : "تست اتصال و اعتبار"}</button>
                    <button className="rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-black text-white disabled:opacity-50" disabled={busy !== null || (providerForm.isEnabled && ((provider?.senderNumberRequired === true && !providerForm.senderNumber.trim()) || (!providerForm.apiKey.trim() && !provider?.hasApiKey)))} onClick={() => void saveProvider()} type="button">{busy === "provider-save" ? "در حال ذخیره…" : "ذخیره امن تنظیمات"}</button>
                  </div>
                </div>
                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <Info label="وضعیت اتصال" value={provider?.lastConnectionTestSucceeded === true ? "سالم" : provider?.lastConnectionTestSucceeded === false ? "ناموفق" : "تست نشده"} />
                  <Info label={`اعتبار (${provider?.creditUnit ?? ""})`} value={provider?.lastKnownCredit == null ? "—" : faNumber(provider.lastKnownCredit, 3)} />
                  <Info label="آخرین تست" value={faDate(provider?.lastConnectionTestAtUtc)} />
                </div>
              </article>
              <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
                <div className="flex items-start justify-between gap-3">
                  <div><h2 className="text-lg font-black text-ink-950">خطوط ارسال {provider?.provider}</h2><p className="mt-1 text-xs leading-6 text-slate-400">خطوط قابل شناسایی حساب را دریافت و خط پیش‌فرض را انتخاب کنید.</p></div>
                  <button className="rounded-xl bg-slate-950 px-3 py-2.5 text-xs font-black text-white disabled:opacity-50" disabled={busy !== null || !provider?.hasApiKey || !provider?.supportsLineDiscovery} onClick={() => void syncLines()} type="button">{busy === "lines" ? "در حال دریافت…" : "دریافت خطوط"}</button>
                </div>
                <div className="mt-5 space-y-2">
                  {lines.length ? lines.map((line) => <button className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-right transition ${providerForm.senderNumber === line.lineNumber ? "border-brand-300 bg-brand-50" : "border-slate-100 hover:bg-slate-50"}`} key={line.lineNumber} onClick={() => setProviderForm((current) => ({ ...current, senderNumber: line.lineNumber }))} type="button"><span className="font-sans text-sm font-bold" dir="ltr">{line.lineNumber}</span>{providerForm.senderNumber === line.lineNumber ? <span className="text-xs font-black text-brand-600">انتخاب‌شده</span> : null}</button>) : <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-xs leading-6 text-slate-400">پس از ذخیره API Key، دکمه «دریافت خطوط» را بزنید.</div>}
                </div>
                <div className="mt-5 rounded-2xl bg-slate-950 p-4 text-white"><p className="text-xs font-bold text-slate-300">آدرس ثابت سرویس</p><p className="mt-2 break-all text-left font-sans text-xs" dir="ltr">{provider?.baseUrl}</p></div>
              </article>
            </section>
          </div>
        ) : null}

        {!loading && tab === "templates" ? (
          <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)]">
            <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <h2 className="font-black text-ink-950">انتخاب شرکت و سناریو</h2>
              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-bold text-slate-500">
                  شرکت
                </span>
                <select
                  className="input"
                  onChange={(event) => {
                    setSelectedTenantId(event.target.value);
                    setAutomationLoadedFor("");
                  }}
                  value={selectedTenantId}
                >
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="mt-5 space-y-3">
                <SettingToggle
                  label="فعال بودن ارسال خودکار بعد از ثبت سرنخ"
                  checked={automation.isEnabled}
                  onChange={() =>
                    setAutomation((current) => ({
                      ...current,
                      isEnabled: !current.isEnabled,
                    }))
                  }
                />
                <SettingToggle
                  label="ارسال پیامک"
                  checked={automation.smsEnabled}
                  disabled={!canConfigureTenantSms}
                  onChange={() =>
                    setAutomation((current) => ({
                      ...current,
                      smsEnabled: !current.smsEnabled,
                    }))
                  }
                />
                <SettingToggle
                  label="ارسال ایمیل"
                  checked={automation.emailEnabled}
                  disabled={!canConfigureTenantEmail}
                  onChange={() =>
                    setAutomation((current) => ({
                      ...current,
                      emailEnabled: !current.emailEnabled,
                    }))
                  }
                />
                <SettingToggle
                  label="ارسال شبکه اجتماعی"
                  checked={automation.socialEnabled}
                  disabled={!canConfigureTenantSocial}
                  onChange={() =>
                    setAutomation((current) => ({
                      ...current,
                      socialEnabled: !current.socialEnabled,
                    }))
                  }
                />
              </div>
              {!canConfigureTenantEmail ? (
                <p className="mt-4 rounded-xl bg-amber-50 p-3 text-xs font-bold leading-6 text-amber-700">
                  مجوز ایمیل این شرکت در بخش «مدیریت شرکت‌ها و لایسنس‌ها»
                  غیرفعال است.
                </p>
              ) : null}
              {!canConfigureTenantSocial ? (
                <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs font-bold leading-6 text-amber-700">
                  مجوز شبکه اجتماعی این شرکت در بخش «مدیریت شرکت‌ها و لایسنس‌ها»
                  غیرفعال است.
                </p>
              ) : null}
              {!canConfigureTenantSms ? (
                <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs font-bold leading-6 text-amber-700">
                  برای این شرکت ابتدا مجوز و قابلیت شارژ پیامک را فعال کنید.
                </p>
              ) : selectedAutomationTenant &&
                !hasAvailableSmsCredit(selectedAutomationTenant) ? (
                <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs font-bold leading-6 text-amber-700">
                  شارژ پیامک شرکت به پایان رسیده است؛ سناریو ذخیره می‌شود اما تا
                  افزایش شارژ ارسالی انجام نخواهد شد.
                </p>
              ) : null}
            </article>
            <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-black text-ink-950">
                    قالب معرفی سرنخ شرکت
                  </h2>
                  <p className="mt-1 text-xs text-slate-400">
                    لینک کاتالوگ در زمان ارسال پیامک به متن افزوده می‌شود.
                  </p>
                </div>
                <button
                  className="rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-black text-white disabled:opacity-50"
                  disabled={
                    busy !== null ||
                    !selectedTenantId ||
                    !automation.messageTemplate.trim()
                  }
                  onClick={() => void saveAutomation()}
                  type="button"
                >
                  {busy === "automation-save"
                    ? "در حال ذخیره…"
                    : "ذخیره سناریو و قالب"}
                </button>
              </div>
              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-bold text-slate-500">
                  موضوع ایمیل
                </span>
                <input
                  className="input"
                  maxLength={200}
                  onChange={(event) =>
                    setAutomation((current) => ({
                      ...current,
                      messageSubject: event.target.value,
                    }))
                  }
                  value={automation.messageSubject}
                />
              </label>
              <label className="mt-4 block">
                <span className="mb-2 flex items-center justify-between text-xs font-bold text-slate-500">
                  <span>متن پیام</span>
                  <span>
                    {faNumber(automation.messageTemplate.length)} / ۲٬۰۰۰
                  </span>
                </span>
                <textarea
                  className="min-h-52 w-full resize-y rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-8 outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                  maxLength={2000}
                  onChange={(event) =>
                    setAutomation((current) => ({
                      ...current,
                      messageTemplate: event.target.value,
                    }))
                  }
                  value={automation.messageTemplate}
                />
              </label>
              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  "{{contactName}}",
                  "{{companyName}}",
                  "{{chiefExecutiveName}}",
                  "{{tenantName}}",
                  "{{companyLink}}",
                  "{{catalogLink}}",
                ].map((token) => (
                  <button
                    className="rounded-lg bg-brand-50 px-2.5 py-1.5 font-sans text-[11px] font-bold text-brand-600"
                    key={token}
                    onClick={() =>
                      setAutomation((current) => ({
                        ...current,
                        messageTemplate: `${current.messageTemplate}${current.messageTemplate.endsWith(" ") ? "" : " "}${token}`,
                      }))
                    }
                    type="button"
                  >
                    {token}
                  </button>
                ))}
              </div>
            </article>
          </section>
        ) : null}

        {!loading && tab === "send" ? (
          <section className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
            <div className="flex items-start gap-3">
              <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600">
                <AppIcon name="send" size={20} />
              </span>
              <div>
                <h2 className="text-xl font-black text-ink-950">
                  ارسال پیامک آزمایشی
                </h2>
                <p className="mt-1 text-sm leading-7 text-slate-500">
                  پیام از سهم و مجوز شرکت انتخابی و با خط مرکزی ارسال و در
                  گزارش‌ها ثبت می‌شود.
                </p>
              </div>
            </div>
            {!provider?.isConfigured ? (
              <p className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs font-bold leading-6 text-amber-700">
                ابتدا در تب «سرویس‌دهنده و خطوط»، API Key و خط پیش‌فرض را ذخیره
                و سرویس را فعال کنید.
              </p>
            ) : smsReadyCount === 0 ? (
              <p className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs font-bold leading-6 text-amber-700">
                هیچ شرکتی هم‌زمان مجوز و شارژ قابل استفاده ندارد. ابتدا دسترسی و
                اعتبار یک شرکت را فعال کنید.
              </p>
            ) : null}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label>
                <span className="mb-2 block text-xs font-bold text-slate-500">
                  شرکت
                </span>
                <select
                  className="input"
                  onChange={(event) =>
                    setSendForm((current) => ({
                      ...current,
                      tenantId: event.target.value,
                    }))
                  }
                  value={sendForm.tenantId}
                >
                  <option value="">انتخاب شرکت</option>
                  {tenants.map((tenant) => (
                    <option
                      disabled={!hasAvailableSmsCredit(tenant)}
                      key={tenant.id}
                      value={tenant.id}
                    >
                      {tenant.name}
                      {hasAvailableSmsCredit(tenant)
                        ? tenant.subscription.smsCreditUnlimited
                          ? " — نامحدود"
                          : ` — ${faNumber(tenant.subscription.smsCreditRemaining)} پیامک`
                        : " — بدون مجوز یا شارژ"}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="mb-2 block text-xs font-bold text-slate-500">
                  شماره موبایل گیرنده
                </span>
                <input
                  className="input"
                  dir="ltr"
                  inputMode="tel"
                  onChange={(event) =>
                    setSendForm((current) => ({
                      ...current,
                      mobile: event.target.value,
                    }))
                  }
                  placeholder="0912..."
                  value={sendForm.mobile}
                />
              </label>
            </div>
            <label className="mt-4 block">
              <span className="mb-2 flex items-center justify-between text-xs font-bold text-slate-500">
                <span>متن پیامک</span>
                <span>{faNumber(sendForm.message.length)} کاراکتر</span>
              </span>
              <textarea
                className="min-h-40 w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-8 outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                maxLength={2000}
                onChange={(event) =>
                  setSendForm((current) => ({
                    ...current,
                    message: event.target.value,
                  }))
                }
                value={sendForm.message}
              />
            </label>
            <button
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-3 text-sm font-black text-white disabled:opacity-50 sm:w-auto"
              disabled={
                busy !== null ||
                !canSendTest ||
                !sendForm.mobile.trim() ||
                !sendForm.message.trim()
              }
              onClick={() => void sendTest()}
              type="button"
            >
              <AppIcon name="send" size={17} />
              {busy === "send" ? "در حال ارسال…" : "ارسال و ثبت نتیجه"}
            </button>
          </section>
        ) : null}

        {!loading && tab === "reports" ? (
          <div className="space-y-5">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                <label>
                  <span className="mb-2 block text-xs font-bold text-slate-500">
                    شرکت
                  </span>
                  <select
                    className="input"
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        tenantId: event.target.value,
                      }))
                    }
                    value={filters.tenantId}
                  >
                    <option value="">همه شرکت‌ها</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="mb-2 block text-xs font-bold text-slate-500">
                    وضعیت
                  </span>
                  <select
                    className="input"
                    onChange={(event) =>
                      setFilters((current) => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                    value={filters.status}
                  >
                    <option value="">همه وضعیت‌ها</option>
                    {Object.entries(statusMeta).map(([value, meta]) => (
                      <option key={value} value={value}>
                        {meta.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  className="self-end rounded-xl bg-slate-950 px-5 py-3 text-xs font-black text-white disabled:opacity-50"
                  disabled={busy !== null}
                  onClick={() => void refreshReports()}
                  type="button"
                >
                  {busy === "reports" ? "در حال دریافت…" : "اعمال فیلتر"}
                </button>
              </div>
            </section>
            <ResponsiveDeliveryTable
              deliveries={deliveries}
              tenantNames={tenantNames}
              busy={busy}
              onRetry={retry}
            />
          </div>
        ) : null}
      </div>
    </main>
  );
};

interface DeliveryTableProps {
  deliveries: PlatformNotificationDelivery[];
  tenantNames: Record<string, string>;
  busy: string | null;
  onRetry: (id: string) => Promise<void>;
  compact?: boolean;
}

const ResponsiveDeliveryTable = ({
  deliveries,
  tenantNames,
  busy,
  onRetry,
  compact = false,
}: DeliveryTableProps): React.ReactElement => (
  <>
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:hidden">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-black text-ink-950">
            {compact ? "آخرین پیامک‌ها" : "صف و گزارش ارسال"}
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            نتیجه واقعی هر تلاش و سرویس‌دهنده استفاده‌شده
          </p>
        </div>
        <span className="w-fit shrink-0 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500">
          {faNumber(deliveries.length)} پیامک
        </span>
      </div>
      {deliveries.length ? (
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {deliveries.map((delivery) => {
            const meta = statusMeta[delivery.status] ?? {
              label: delivery.status,
              tone: "bg-slate-100 text-slate-600",
            };
            return (
              <article
                className="min-w-0 rounded-2xl border border-slate-100 bg-slate-50/70 p-4"
                key={delivery.id}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-black text-slate-700">
                      {tenantNames[delivery.tenantId] ?? delivery.tenantId}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-400">
                      {faDate(delivery.createdAtUtc)}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ${meta.tone}`}
                  >
                    {meta.label}
                  </span>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl bg-white p-3">
                    <dt className="text-slate-400">گیرنده</dt>
                    <dd
                      className="mt-1 truncate font-sans font-bold text-slate-700"
                      dir="ltr"
                    >
                      {delivery.recipient ?? "—"}
                    </dd>
                  </div>
                  <div className="rounded-xl bg-white p-3">
                    <dt className="text-slate-400">تعداد تلاش</dt>
                    <dd className="mt-1 font-black text-slate-700">
                      {faNumber(delivery.attemptCount)}
                    </dd>
                  </div>
                  <div className="col-span-2 rounded-xl bg-white p-3">
                    <dt className="text-slate-400">سرویس‌دهنده / شناسه پیام</dt>
                    <dd className="mt-1 font-bold text-slate-700">
                      {providerLabel(delivery.providerCode)}
                      {delivery.externalMessageId ? <span className="mr-2 font-sans text-[10px] text-slate-400" dir="ltr">#{delivery.externalMessageId}</span> : null}
                    </dd>
                  </div>
                </dl>
                <p className="mt-3 line-clamp-3 break-words rounded-xl bg-white p-3 text-xs leading-6 text-slate-500">
                  {delivery.error ?? delivery.message}
                </p>
                {delivery.status !== "sent" && delivery.recipient ? (
                  <button
                    className="mt-3 w-full rounded-xl bg-brand-50 px-3 py-2.5 text-xs font-black text-brand-600 disabled:opacity-50"
                    disabled={busy !== null}
                    onClick={() => void onRetry(delivery.id)}
                    type="button"
                  >
                    {busy === `retry-${delivery.id}`
                      ? "در حال تلاش…"
                      : "تلاش مجدد"}
                  </button>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
          هنوز پیامکی ثبت نشده است.
        </div>
      )}
    </section>
    <div className="hidden lg:block">
      <DeliveryTable
        busy={busy}
        compact={compact}
        deliveries={deliveries}
        onRetry={onRetry}
        tenantNames={tenantNames}
      />
    </div>
  </>
);

const Info = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement => (
  <div className="rounded-2xl bg-slate-50 p-3">
    <p className="text-[10px] font-bold text-slate-400">{label}</p>
    <p className="mt-1 text-xs font-black text-slate-700">{value}</p>
  </div>
);

const SettingToggle = ({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}): React.ReactElement => (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 p-4">
    <span className="text-xs font-black leading-6 text-slate-600">{label}</span>
    <Toggle
      checked={checked}
      disabled={disabled}
      label={label}
      onChange={onChange}
    />
  </div>
);

const DeliveryTable = ({
  deliveries,
  tenantNames,
  busy,
  onRetry,
  compact = false,
}: {
  deliveries: PlatformNotificationDelivery[];
  tenantNames: Record<string, string>;
  busy: string | null;
  onRetry: (id: string) => Promise<void>;
  compact?: boolean;
}): React.ReactElement => (
  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="font-black text-ink-950">
          {compact ? "آخرین پیامک‌ها" : "صف و گزارش ارسال"}
        </h2>
        <p className="mt-1 text-xs text-slate-400">
          نتیجه واقعی هر تلاش و سرویس‌دهنده استفاده‌شده
        </p>
      </div>
      <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500">
        {faNumber(deliveries.length)} پیامک
      </span>
    </div>
    {deliveries.length ? (
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[980px] text-right text-xs">
          <thead className="border-y border-slate-100 text-slate-400">
            <tr>
              <th className="py-3">زمان</th>
              <th>شرکت</th>
              <th>گیرنده</th>
              <th>وضعیت</th>
              <th>سرویس‌دهنده</th>
              <th>تلاش</th>
              <th>نتیجه</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {deliveries.map((delivery) => {
              const meta = statusMeta[delivery.status] ?? {
                label: delivery.status,
                tone: "bg-slate-100 text-slate-600",
              };
              return (
                <tr
                  className="border-b border-slate-50 align-top last:border-0"
                  key={delivery.id}
                >
                  <td className="py-4 text-slate-500">
                    {faDate(delivery.createdAtUtc)}
                  </td>
                  <td className="py-4 font-black text-slate-700">
                    {tenantNames[delivery.tenantId] ?? delivery.tenantId}
                  </td>
                  <td className="py-4 font-sans text-slate-500" dir="ltr">
                    {delivery.recipient ?? "—"}
                  </td>
                  <td className="py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 font-bold ${meta.tone}`}
                    >
                      {meta.label}
                    </span>
                  </td>
                  <td className="py-4 font-bold text-slate-600">
                    {providerLabel(delivery.providerCode)}
                    {delivery.externalMessageId ? <span className="mt-1 block max-w-28 truncate font-sans text-[10px] text-slate-400" dir="ltr">#{delivery.externalMessageId}</span> : null}
                  </td>
                  <td className="py-4 text-slate-500">
                    {faNumber(delivery.attemptCount)}
                  </td>
                  <td className="max-w-72 py-4">
                    <p className="line-clamp-2 leading-6 text-slate-500">
                      {delivery.error ?? delivery.message}
                    </p>
                  </td>
                  <td className="py-4">
                    {delivery.status !== "sent" && delivery.recipient ? (
                      <button
                        className="rounded-lg bg-brand-50 px-3 py-2 font-black text-brand-600 disabled:opacity-50"
                        disabled={busy !== null}
                        onClick={() => void onRetry(delivery.id)}
                        type="button"
                      >
                        {busy === `retry-${delivery.id}`
                          ? "در حال تلاش…"
                          : "تلاش مجدد"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="mt-5 rounded-2xl border border-dashed border-slate-200 p-10 text-center text-sm text-slate-400">
        هنوز پیامکی ثبت نشده است.
      </div>
    )}
  </section>
);
